/**
 * The Results tab — per-funnel performance, framed in leads.
 *
 * Visual Reference: `design/screens/editor-results.html`, four states —
 * default, loading, empty, error.
 *
 * PRD §7.5: "**Results** | Per-funnel leads table and analytics | M1", and
 * §1.9's rule holds here as everywhere: the headline is a SENTENCE — "412
 * people finished this funnel. 74 were worth calling." — and the tiles follow
 * it. A figure never leads.
 *
 * DROP-OFF BY STEP DOES NOT RENDER. The mockup draws it in full — five steps
 * with continued counts and percentages — and `docs/api-contract.md` dates
 * `GET …/dropoff` to **M6**. DECISION-016: "features dated to later milestones
 * do not render at M1… a `$0.00` cost tile reads as broken rather than as
 * not-yet-built." A drop-off panel showing five steps of nothing would read
 * worse than one that is not there. RULE-052 governs its framing when it
 * arrives — leads, not only percentages.
 *
 * Story 8.7.
 */
import { useCallback, useEffect, useState } from 'react'
import { apiGet, ApiFailure } from '../lib/api'
import { useShell } from '../shell/workspace'
import { Button } from '../ui/Button'
import { Pill } from '../ui/Pill'
import { Skeleton } from '../ui/Skeleton'
import { LeadRow, type Lead } from '../pages/Leads'

type BandRow = {
  id: string
  label: string
  tone: 'hot' | 'review' | 'nurture'
  leads: number
  share: number
}

type Results = {
  range: string
  started: number
  finished: number
  completionRate: number
  qualified: number
  qualifiedRate: number
  bands: BandRow[]
  overCap: number
  verdict: string
}

const RANGES: [string, string][] = [
  ['7d', 'Last 7 days'],
  ['30d', 'Last 30 days'],
  ['90d', 'Last 90 days'],
  ['all', 'All time'],
]

const PILL_TONE = { hot: 'hot', review: 'review', nurture: 'neutral' } as const

const BAR_TONE: Record<BandRow['tone'], string> = {
  hot: 'bg-mdx-hot',
  review: 'bg-mdx-review',
  nurture: 'bg-mdx-nurture',
}

export function ResultsTab({ funnelId }: { funnelId: string }) {
  const { workspace } = useShell()
  const workspaceId = workspace?.id ?? null

  const [range, setRange] = useState('30d')
  const [results, setResults] = useState<Results | null>(null)
  const [leads, setLeads] = useState<Lead[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!workspaceId) return
    setError(null)
    setResults(null)
    setLeads(null)
    try {
      /**
       * Two reads, one screen. The rollup is an aggregate and the table is a
       * page of rows — asking one query to be both would mean either counting
       * in JavaScript (RULE-056's cost bug) or paging an aggregate.
       *
       * The table comes from the LEADS list route, filtered to this funnel, so
       * every row has been through `redactLead` exactly as it is on the leads
       * screen. A second query here that read `leads` directly would be a sixth
       * exit, which is the thing `LEAD_EXITS` exists to prevent.
       */
      const [rollup, page] = await Promise.all([
        apiGet<Results>(`/v1/w/${workspaceId}/funnels/${funnelId}/results?range=${range}`),
        apiGet<{ leads: Lead[] }>(`/v1/w/${workspaceId}/leads?funnel=${funnelId}`),
      ])
      setResults(rollup)
      setLeads(page.leads)
    } catch (e) {
      setError(
        e instanceof ApiFailure
          ? e.message
          : "We couldn't read this funnel's results. Its leads are still captured — try again.",
      )
    }
  }, [workspaceId, funnelId, range])

  useEffect(() => {
    void load()
  }, [load])

  if (error) {
    return (
      <div className="flex flex-col items-start gap-mdx-12 px-mdx-24 py-mdx-48">
        <h2 className="m-0 text-mdx-20 font-mdx-bold tracking-mdx-h3 text-mdx-ink">
          We couldn&rsquo;t read the results
        </h2>
        <p className="m-0 max-w-mdx-measure text-mdx-15 text-mdx-slate">{error}</p>
        <Button onClick={() => void load()}>Try again</Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-mdx-24 px-mdx-24 py-mdx-24">
      <header className="flex flex-wrap items-end justify-between gap-mdx-16">
        <div className="flex flex-col gap-mdx-4">
          <span className="text-mdx-12 font-mdx-bold uppercase tracking-mdx-eyebrow text-mdx-slate">
            Results
          </span>
          {/* The Verdict. PRD §1.9 — a sentence, then the numbers. */}
          <h2 className="m-0 max-w-mdx-measure text-mdx-25 font-mdx-bold leading-mdx-tight tracking-mdx-h1 text-mdx-ink">
            {results ? results.verdict : <Skeleton className="h-mdx-28 w-full rounded-mdx-sm" />}
          </h2>
        </div>

        <label className="flex items-center gap-mdx-8">
          <span className="sr-only">Range</span>
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            aria-label="Range"
            className="h-mdx-input cursor-pointer rounded-mdx-control border border-mdx-line bg-mdx-white px-mdx-12 text-mdx-15 font-mdx-semibold text-mdx-ink outline-none focus:border-mdx-brand"
          >
            {RANGES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </header>

      <div className="grid grid-cols-2 gap-mdx-12 md:grid-cols-4">
        <Tile label="Started" value={results?.started} />
        <Tile label="Finished" value={results?.finished} />
        <Tile label="Completion rate" value={results?.completionRate} suffix="%" />
        <Tile
          label="Qualified"
          value={results?.qualified}
          meta={results ? `${results.qualifiedRate}%` : undefined}
        />
      </div>

      <section className="flex flex-col gap-mdx-12 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-16 py-mdx-16 shadow-mdx-card">
        <div className="flex flex-wrap items-baseline gap-mdx-8">
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3 text-mdx-ink">
            {results ? `Where the ${results.finished.toLocaleString()} landed` : 'Band distribution'}
          </span>
          <span className="text-mdx-13 text-mdx-slate">Band distribution</span>
        </div>

        {results === null ? (
          <div className="flex flex-col gap-mdx-8" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-mdx-24 w-full rounded-mdx-control" />
            ))}
          </div>
        ) : results.finished === 0 ? (
          <span className="text-mdx-15 text-mdx-slate">
            Nothing to split yet. The first finished submission lands here.
          </span>
        ) : (
          <>
            {results.bands.map((band) => (
              <div key={band.id} className="flex flex-wrap items-center gap-mdx-12">
                <Pill tone={PILL_TONE[band.tone]} dot className="w-mdx-row-identity-sm shrink-0">
                  {band.label}
                </Pill>
                <span className="flex h-mdx-8 min-w-0 grow basis-mdx-row-control overflow-hidden rounded-mdx-pill bg-mdx-line-soft">
                  <span
                    className={`h-full rounded-mdx-pill ${BAR_TONE[band.tone]}`}
                    style={{ width: `${band.share}%` }}
                  />
                </span>
                <span className="flex-none text-mdx-13 text-mdx-slate tabular-nums">
                  {band.leads.toLocaleString()} · {band.share}%
                </span>
              </div>
            ))}

            {/*
              DECISION-061, the same rule the funnels list follows: an over-cap
              lead is counted and never banded. Named rather than folded in, so
              the split visibly sums to `finished` and nobody has to wonder
              where the difference went.
            */}
            {results.overCap > 0 ? (
              <span className="text-mdx-13 text-mdx-slate">
                {results.overCap.toLocaleString()} more{' '}
                {results.overCap === 1 ? 'lead is' : 'leads are'} beyond your plan cap and
                aren&rsquo;t banded here.
              </span>
            ) : null}
          </>
        )}
      </section>

      <section className="flex flex-col gap-mdx-8">
        <div className="flex flex-wrap items-center justify-between gap-mdx-12">
          <span className="text-mdx-17 font-mdx-semibold tracking-mdx-h3 text-mdx-ink">
            Leads from this funnel
          </span>
          <a
            href={`/leads?funnel=${encodeURIComponent(funnelId)}`}
            className="text-mdx-15 font-mdx-semibold text-mdx-brand-deep no-underline hover:underline"
          >
            See all leads
          </a>
        </div>

        {/*
          THE SAME ROW, not a second one. `LeadRow` is Story 8.2's component,
          exported rather than copied — the AC asks for "the same row shape as
          the leads list", and the only way to keep that true a year from now is
          for there to be one row. Selection is optional on it, so this surface
          reuses it without a checkbox it has no bulk bar for.
        */}
        <div className="flex flex-col gap-mdx-2 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-12 py-mdx-12 shadow-mdx-card">
          {leads === null ? (
            <div className="flex flex-col gap-mdx-2" aria-busy="true">
              {[0, 1, 2].map((i) => (
                <Skeleton key={i} className="h-mdx-40 w-full rounded-mdx-control" />
              ))}
            </div>
          ) : leads.length === 0 ? (
            <span className="px-mdx-16 py-mdx-14 text-mdx-15 text-mdx-slate">
              No leads from this funnel yet.
            </span>
          ) : (
            leads.slice(0, 6).map((lead) => <LeadRow key={lead.id} lead={lead} />)
          )}
        </div>
      </section>
    </div>
  )
}

function Tile({
  label,
  value,
  suffix,
  meta,
}: {
  label: string
  value: number | undefined
  suffix?: string
  meta?: string
}) {
  return (
    <div className="flex flex-col gap-mdx-4 rounded-mdx-card border border-mdx-line bg-mdx-white px-mdx-16 py-mdx-14 shadow-mdx-card">
      <span className="text-mdx-13 text-mdx-slate">{label}</span>
      {value === undefined ? (
        <Skeleton className="h-mdx-28 w-mdx-row-identity-sm rounded-mdx-sm" />
      ) : (
        <span className="flex items-baseline gap-mdx-8">
          <span className="text-mdx-28 font-mdx-bold text-mdx-ink tabular-nums">
            {value.toLocaleString()}
            {suffix ?? ''}
          </span>
          {meta ? <span className="text-mdx-13 text-mdx-slate tabular-nums">{meta}</span> : null}
        </span>
      )}
    </div>
  )
}
